/**
 * Composing an image URL, which every client does slightly differently.
 *
 * The same four lines were written out in eight places — `${base}${path}` in
 * some, `${base}/${path}` in others — and that difference is not cosmetic:
 * Bajaj's images are served straight from S3, and S3 treats a double slash as
 * a different key. Measured against the real bucket:
 *
 *   .../root/media_staging/Bajaj/<ref>/uploads/<file>.jpg     200
 *   ...//root/media_staging/Bajaj/<ref>/uploads/<file>.jpg    403
 *   .../media_staging/Bajaj/<ref>/uploads/<file>.jpg          403
 *   .../home/camcom/media_staging/Bajaj/<ref>/uploads/...     403
 *
 * So the bucket keys everything under `root/`, while the camcom media hosts
 * serve the API's own filesystem paths (`/home/camcom/media_staging/…`)
 * verbatim. A client on S3 therefore needs its paths re-rooted, which is what
 * `pathPrefix` is for — declared per client, so nothing changes for the hosts
 * that do not need it.
 */

/** A path the browser can already fetch: leave it alone. */
const isAbsolute = (value) => /^(https?:|data:|blob:)/i.test(value);

const MEDIA_MARKER = "media_staging/";

/**
 * @param path      whatever the API returned — "/home/camcom/media_staging/…",
 *                  "/root/media_staging/…", "media_staging/…" or a full URL
 * @param baseUrl   the client's media host
 * @param pathPrefix the key prefix that host expects, e.g. "/root". Omitted for
 *                  hosts that serve the API's paths as they come.
 */
export const buildMediaUrl = (path, { baseUrl = "", pathPrefix = "" } = {}) => {
  if (path === null || path === undefined) return "";
  const value = String(path).trim();
  if (!value) return "";
  if (isAbsolute(value)) return value;

  // One leading slash or three, the key is the same.
  let key = value.replace(/^\/+/, "");

  const prefix = String(pathPrefix || "").replace(/^\/+|\/+$/g, "");
  if (prefix) {
    const at = key.indexOf(MEDIA_MARKER);
    if (at >= 0) {
      // Re-root whatever came before media_staging/. Idempotent: a path that
      // already starts with the prefix comes back unchanged.
      key = `${prefix}/${key.slice(at)}`;
    } else if (key !== prefix && !key.startsWith(`${prefix}/`)) {
      key = `${prefix}/${key}`;
    }
  }

  const base = String(baseUrl || "").replace(/\/+$/, "");
  return base ? `${base}/${key}` : `/${key}`;
};

export default buildMediaUrl;
